import type { LogDescriptor, LogKind, LogState } from '../../core/log/client';
import { PROGRAM_LABEL } from '../../core/loglist/sources';
import { reachability, reachabilityVersion, hasProxy } from '../state/transport';
import { Badge, type Tone } from './Badge';

export function KindBadge({ kind }: { kind: LogKind }) {
  return kind === 'tiled' ? (
    <Badge tone="outline" title="static-ct-api (Sunlight-style tiled log)">tiled</Badge>
  ) : (
    <Badge tone="outline" title="RFC 6962 JSON API">RFC 6962</Badge>
  );
}

export const STATE_TONE: Record<LogState, Tone> = { usable: 'ok', qualified: 'info', readonly: 'warn', pending: 'muted', retired: 'muted', rejected: 'err', unknown: 'muted' };

export function StateBadge({ state, timestamp }: { state: LogState; timestamp?: string }) {
  return (
    <Badge tone={STATE_TONE[state]} title={timestamp ? `${state} since ${timestamp.slice(0, 10)}` : state}>
      {state}
    </Badge>
  );
}

/** One badge per CT program that lists the log: "Chrome usable", "Apple readonly", … */
export function ProgramBadges({ log, compact = false }: { log: LogDescriptor; compact?: boolean }) {
  if (log.programs.length === 0) return <Badge tone="outline" title="Not in any program's list">custom</Badge>;
  return (
    <span class="row" style="gap:0.25rem">
      {log.programs.map((p) => (
        <Badge key={p.program} tone={STATE_TONE[p.state]} title={`${PROGRAM_LABEL[p.program]}: ${p.state}${p.stateTimestamp ? ` since ${p.stateTimestamp.slice(0, 10)}` : ''}${p.description ? ` (listed as "${p.description}")` : ''}`}>
          {compact ? PROGRAM_LABEL[p.program][0] : PROGRAM_LABEL[p.program]} · {p.state}
        </Badge>
      ))}
    </span>
  );
}

/** Whether the browser has been able to reach this log, learned from previous requests. Renders nothing while unknown. */
export function ReachBadge({ logId, showUnknown = false }: { logId: string; showUnknown?: boolean }) {
  void reachabilityVersion.value; // subscribe
  const s = reachability.summary(logId);
  const proxy = hasProxy.value;
  switch (s) {
    case 'direct':
      return <Badge tone="ok" title="Reachable directly from the browser">reachable</Badge>;
    case 'proxy':
      return <Badge tone="info" title="Reachable via the configured proxy / desktop app">via proxy</Badge>;
    case 'partial':
      return <Badge tone="warn" title="Some endpoints are blocked by CORS (often get-entries)">partly blocked</Badge>;
    case 'blocked':
      return <Badge tone="err" title={proxy ? 'Blocked by CORS; proxy also failed' : 'Blocked by CORS; turn on a proxy in Settings or use the desktop app'}>blocked</Badge>;
    default:
      return showUnknown ? <span class="muted tiny">not tried</span> : null;
  }
}
